// The disposition check and the label section of a Process return/refund
// step (2026-10-27, docs/contact-view.md "The disposition check"), shared by
// the main panel's step (SuggestionPanel.jsx) and the agent's
// (ShadowDraftPanel.jsx) so the two can't drift. Pure.
//
//   - The Disposition dropdown holds Return and Scrap only. It is blank only
//     while no check has run (DISPOSITION_HELPER); a check's result is
//     selected; another choice shows "Overridden from <checked>".
//   - Editing the items or the reason code after a check makes it stale
//     (CHECK_STALE_TEXT): Process waits for Check again.
//   - The label fields (send-to, the internal copy, the packages) show where
//     a label is made: the part comes back, on the Website RMA path (no eBay
//     order, no eBay return open), unless the customer ships their own.

import { DISPOSITION_NAMES, DISPOSITION_OPTION_LABELS } from '@cxhub/shared'

export const DISPOSITION_LABEL = 'Disposition (from the scrap check — override if needed)'
export const DISPOSITION_HELPER = 'Choose items and a reason code, then Check'
export const CHECK_STALE_TEXT = 'Check again to update'
// The names (2026-10-28, @cxhub/shared's already-shipped-back.js): "RMA -
// the part comes back" and "Scrap RTN - the customer keeps it", on every
// panel (the pipeline's step, the agent's, Other Actions).
export const DISPOSITION_OPTIONS = Object.freeze([
  { value: 'return', label: DISPOSITION_OPTION_LABELS.return },
  { value: 'scrap', label: DISPOSITION_OPTION_LABELS.scrap },
])
export const MAX_PACKAGE_WEIGHT = 150

export const dispositionWord = (b) => (b === true || b === 'return' ? 'return' : (b === false || b === 'scrap' ? 'scrap' : null))
export const dispositionLabel = (word) => DISPOSITION_NAMES[word] ?? ''

// "Overridden from RMA" (or "Scrap RTN") when the choice differs from the
// check's result, else null. reason (2026-10-28): why the step overrode it
// itself ("the customer says the part was already returned (UPS ...)").
export function overriddenText(chosen, checked, reason = null) {
  const c = dispositionWord(chosen)
  const k = dispositionWord(checked)
  if (!c || !k || c === k) return null
  return `Overridden from ${dispositionLabel(k)}${reason ? `: ${reason}.` : ''}`
}

const itemsKey = (items) => (Array.isArray(items) ? items : [])
  .filter((i) => i?.sku)
  .map((i) => `${String(i.sku).trim().toUpperCase()}x${Number(i.quantity) || 1}`)
  .sort()
  .join(',')

// Whether a check ran for exactly these items and this reason code. A check
// that does not say what it ran for (an older answer) counts as current.
export function checkMatchesInputs(check, input) {
  if (!check) return false
  const code = check.reasonCode ?? check.checkedReasonCode
  const items = check.items ?? check.checkedItems
  if (code == null && !Array.isArray(items)) return true
  if (code != null && String(code) !== String(input?.reasonCode ?? '')) return false
  if (Array.isArray(items) && itemsKey(items) !== itemsKey(input?.items)) return false
  return true
}

// Stale: a check ran, and the items or the reason code changed since.
export function checkIsStale(check, input) {
  return Boolean(check) && !checkMatchesInputs(check, input)
}

// The main panel's dispositionOverride for a dropdown choice: undefined when
// it is the check's own result, else the boolean (true return, false scrap)
// the executor applies.
export function overrideForChoice(choice, naturalDisposition) {
  const want = choice === 'return'
  return want === (naturalDisposition === true) ? undefined : want
}

// Whether the label fields show (the same rule on both panels).
//   disposition: true / 'return' when the part comes back.
// alreadyShippedBack (2026-10-28): the customer already sent the part back
// and the step keeps the customer's own shipping: no label to make.
export function returnLabelFieldsShown({ disposition, hasExistingReturn = false, ebay = false, shippingPaymentOption = null, alreadyShippedBack = false } = {}) {
  if (dispositionWord(disposition) !== 'return') return false
  if (alreadyShippedBack && (shippingPaymentOption == null || shippingPaymentOption === 'customerShipsOwn')) return false
  if (hasExistingReturn || ebay) return false
  return shippingPaymentOption !== 'customerShipsOwn'
}

const EMAIL_RE = /^[^\s@,;<>]+@[^\s@,;<>]+\.[^\s@,;<>]+$/
export const isOneEmail = (s) => EMAIL_RE.test(String(s ?? '').trim())

// The label fields' problems, by field ({} when none): the send-to must be
// one email address; the copy's address one email or empty (the brand's
// service@); each package's weight more than 0 and at most 150 lb.
export function labelFieldErrors({ email, ccInternalCopy = true, ccAddress, packages } = {}) {
  const out = {}
  if (!isOneEmail(email)) out.email = 'Enter one email address.'
  if (ccInternalCopy !== false && String(ccAddress ?? '').trim() && !isOneEmail(ccAddress)) out.ccAddress = 'Enter one email address, or leave it empty for the brand\'s.'
  ;(Array.isArray(packages) ? packages : []).forEach((p, i) => {
    const w = Number(p?.weight)
    if (p?.weight === '' || p?.weight == null || !Number.isFinite(w) || w <= 0 || w > MAX_PACKAGE_WEIGHT) out[`package${i}`] = `Package ${i + 1}: a weight over 0 and at most ${MAX_PACKAGE_WEIGHT} lb.`
  })
  return out
}

// The label section's field list for a step, in order: what both panels
// render (the test that they match reads this).
export function labelSectionFields({ shown, showEmail = true }) {
  if (!shown) return []
  return ['packages', ...(showEmail ? ['email', 'ccInternalCopy', 'ccAddress'] : [])]
}

// The main panel's check state after an edit of the items or the reason
// code: a return step keeps a check that says what it ran for (it shows as
// stale, "Check again"); anything else is cleared, as before.
export function keepCheckWhenStale(action, prev, i) {
  if (action?.type === 'process_return_refund' && Array.isArray(prev?.[i]?.result?.checkedItems)) return prev
  return { ...prev, [i]: undefined }
}
